import { add, isDate } from 'date-fns'
import * as Yup from 'yup'
import { ValidationError } from 'yup'
import { _ } from './localization'

// https://www.regextester.com/94502
// eslint-disable-next-line no-useless-escape
export const urlRegex = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/

// URL validation
const url = Yup.string().matches(
    urlRegex,
    { excludeEmptyString: true, message: _('form.validation.invalid_url', {}, false) }
)

// StartDate and EndDate Validation For Program
const endDateProgram = Yup.date().required()
    .when(
        'startDate',
        (startDate, schema) => (
            startDate && (
                schema.min(
                    startDate,
                    _('form.validation.date.enddate_must_later_startdate', {}, false)
                )
            )
        )
    )
const endsAt = Yup.date().required()
    .when(
        'startsAt',
        (startsAt, schema) => (
            startsAt && (
                schema.min(
                    add(startsAt, { seconds: 1 }),
                    _('form.validation.date.enddate_must_later_startdate', {}, false)
                )
            )
        )
    )

// eslint-disable-next-line max-len
const embeddedUrlVideoRegex = /^(https|http):\/\/(?:www\.)?(youtube.com\/embed\/[A-z0-9]+)|(player\.vimeo\.com\/video\/\w+)/
const videoEmbeddedUrl = Yup.string()
    .matches(
        embeddedUrlVideoRegex,
        { excludeEmptyString: true, message: _('form.validation.invalid_video_embedded_url', {}, false) }
    )

// eslint-disable-next-line no-useless-escape
export const colorRegex = /^(?:#|0x)(?:[a-fA-F0-9]{3}|[a-fA-F0-9]{6})$\b|^(?:rgb|hsl)a?\([^\)]*\)$/
const color = Yup.string().matches(
    colorRegex,
    { excludeEmptyString: true, message: _('form.validation.invalid_color', {}, false) }
)
const externalId = Yup.string().max(50).matches(
    /^[a-zA-Z0-9_.]+$/,
    { excludeEmptyString: true, message: _('form.validation.invalid_external_id', {}, false) }
)

// eslint-disable-next-line no-useless-escape
// eslint-disable-next-line max-len
const timeRegex = /(^(?:([01]\d|2[0123])|\d):(?:([012345]\d)|\d)(:[012345]\d)?$)|(^(1[0-2]|0?[1-9]):[0-5][0-9](:[0-5][0-9])? (AM|PM)$)/i
const time = Yup.string().matches(timeRegex, _('form.validation.invalid_time', {}, false))
const datePattern = /(\d{1,2})[/-](\d{1,2})[/-](\d{4})/

const excelBooleanRegex = /^[01yntfYNTF]?$/
const excelBoolean = Yup.string()
    .matches(
        excelBooleanRegex,
        { message: _('form.validation.invalid_excel_boolean', {}, false), excludeEmptyString: true }
    )

const excelArrayIdsRegex = /^[0-9, ]*$/
const excelArrayIds = Yup.string()
    .matches(excelArrayIdsRegex, _('form.validation.invalid_excel_array_ids', {}, false))
const excelExternalId = Yup.string().max(50).matches(/^[a-zA-Z0-9_.]+$/, 'Invalid format').required()

export const parseDateString = (value, originalValue) => {
    if (!originalValue) return null
    if (typeof originalValue === 'string') {
        return new Date(originalValue.replace(datePattern, '$3/$2/$1'))
    }
    return originalValue
}

export const parseTimeString = (value, originalValue) => {
    if (!originalValue) return null
    if (typeof originalValue === 'string') {
        /**
         * The time string is something likes 00/01/1900 09:00
         * We need a dummy date string to concat that time
         * Then we convert it to date object
         * When converting this time field to submit,
         * we only care about hour, min, sec by using getHours(), getMinutes(), getSeconds()
         */
        return new Date(`2020-01-01 ${originalValue.split(' ')[1]}`)
    }
    if (isDate(originalValue)) {
        const date = new Date()
        date.setHours(originalValue?.getHours?.())
        date.setMinutes(originalValue?.getMinutes?.())
        date.setSeconds(originalValue?.getSeconds?.())
        return date
    }
    return originalValue
}

type ErrorObject = {
    [field: string]: string[];
  };
  /**
   * Convert yup error into an error object where the keys are the fields and the values are the errors for that field
   * Note: Only work when use {abortEarly: false} option. -> Yup.validate(obj, { abortEarly: false })
   * @param {ValidationError} err The yup error to convert
   * @returns {ErrorObject} The error object
   */
export function yupErrorToErrorObject(err: ValidationError): ErrorObject {
    const object: ErrorObject = {}
    err?.inner?.forEach?.((x) => {
        if (x.path !== undefined) {
            object[x.path] = x.errors
        }
    })

    return object
}

export default {
    endDateProgram,
    url,
    videoEmbeddedUrl,
    color,
    endsAt,
    time,
    externalId,
    excelBoolean,
    excelArrayIds,
    excelExternalId,
}
